import { Eye } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { StoreOrderDetailModal } from '../components/orders/StoreOrderDetailModal'
import { FilterValues, GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Badge } from '../components/ui/badge'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { cashRemaining } from '../lib/store-order-cash'
import {
  decideStaffCancellation,
  formatStoreOrderDate,
  formatStoreOrderMoney,
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
  getStaffOrderStatusLabel,
  getStaffStoreOrdersProblem,
  getStoreOrderPaymentTypeLabel,
  listStaffCancellationRequests,
  listStaffStoreOrders,
  listStoreOrderCashCollections,
  PAYMENT_STATUS_OPTIONS,
  remitStoreOrderCash,
  resolveStoreOrderCashDiscrepancy,
  type StaffCancellationQueueItem,
  type StaffStoreOrderSummary,
  type StoreOrderCashCollectionSummary,
  type StoreOrderPagination,
} from '../services/store-orders.service'
import { useSessionStore } from '../store/auth/auth.store'

const ITEMS_PER_PAGE = 10

// ocultamiento en pos
const SHOW_CANCELLATION_REQUESTS_TAB = false

const ORDER_STATUS_OPTIONS = [
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'PREPARING', label: 'En preparación' },
  { value: 'READY_FOR_PICKUP', label: 'Listo para recoger' },
  { value: 'OUT_FOR_DELIVERY', label: 'En ruta' },
  { value: 'COMPLETED', label: 'Ticket generado' },
  { value: 'COMPLETED_ORDER', label: 'Pedido completado' },
  { value: 'CANCELLED', label: 'Cancelado' },
]

const CANCELLATION_STATUS_OPTIONS = [
  { value: 'REQUESTED', label: 'Pendiente' },
  { value: 'APPROVED', label: 'Aprobada' },
  { value: 'REJECTED', label: 'Rechazada' },
  { value: 'COMPLETED', label: 'Completada' },
]

const CASH_STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'IN_TRANSIT', label: 'Efectivo en tránsito' },
  { value: 'PARTIALLY_REMITTED', label: 'Rendido parcialmente' },
  { value: 'RECONCILED', label: 'Conciliado' },
  {
    value: 'RECONCILED_WITH_SHORTFALL',
    label: 'Conciliado con diferencia',
  },
]

const EMPTY_PAGINATION: StoreOrderPagination = {
  current_page: 1,
  last_page: 1,
  per_page: ITEMS_PER_PAGE,
  total: 0,
  from: null,
  to: null,
}

function getPaymentMethodsLabel(order: StaffStoreOrderSummary) {
  const labels = (order.payments ?? []).map(
    (payment) =>
      payment.method.name || getStoreOrderPaymentTypeLabel(payment.type),
  )

  return [...new Set(labels)].join(' + ') || 'Por confirmar'
}

function getFulfillmentTypeLabel(
  type: StaffStoreOrderSummary['fulfillment_type'],
) {
  return type === 'PICKUP'
    ? 'Recolección en sucursal'
    : 'Entrega a domicilio'
}

export function StoreOrders() {
  const canResolveCancellations = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cancellation']?.resolve),
  )
  const canViewCash = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cash']?.view),
  )
  const canRemitCash = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cash']?.remit),
  )
  const canResolveCash = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cash']?.resolve),
  )
  const [activeTab, setActiveTab] = useState<
    'orders' | 'cancellations' | 'cash'
  >('orders')
  const [filters, setFilters] = useState<FilterValues>({
    search: '',
    branch: '',
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
  })
  const [page, setPage] = useState(1)
  const [orders, setOrders] = useState<StaffStoreOrderSummary[]>([])
  const [pagination, setPagination] =
    useState<StoreOrderPagination>(EMPTY_PAGINATION)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(
    null,
  )

  const [cancellationFilters, setCancellationFilters] =
    useState<FilterValues>({ branch: '', status: 'REQUESTED' })
  const [cancellationPage, setCancellationPage] = useState(1)
  const [cancellations, setCancellations] = useState<
    StaffCancellationQueueItem[]
  >([])
  const [cancellationPagination, setCancellationPagination] =
    useState<StoreOrderPagination>(EMPTY_PAGINATION)
  const [cancellationsLoading, setCancellationsLoading] = useState(false)
  const [cancellationsError, setCancellationsError] = useState('')
  const [decision, setDecision] = useState<{
    cancellation: StaffCancellationQueueItem
    action: 'approve' | 'reject'
  } | null>(null)
  const [decisionReason, setDecisionReason] = useState('')
  const [decisionError, setDecisionError] = useState('')
  const [savingDecision, setSavingDecision] = useState(false)
  const [cashStatus, setCashStatus] = useState('all')
  const [cashPage, setCashPage] = useState(1)
  const [cashCollections, setCashCollections] = useState<
    StoreOrderCashCollectionSummary[]
  >([])
  const [cashPagination, setCashPagination] =
    useState<StoreOrderPagination>(EMPTY_PAGINATION)
  const [cashLoading, setCashLoading] = useState(false)
  const [cashError, setCashError] = useState('')
  const [cashAction, setCashAction] = useState<{
    collection: StoreOrderCashCollectionSummary
    type: 'remit' | 'discrepancy'
  } | null>(null)
  const [cashActionAmount, setCashActionAmount] = useState('')
  const [cashActionReason, setCashActionReason] = useState('')
  const [cashActionError, setCashActionError] = useState('')
  const [savingCashAction, setSavingCashAction] = useState(false)

  const loadOrders = useCallback(async () => {
    setIsLoading(true)
    setError('')
    const branchId = Number(filters.branch)
    try {
      const result = await listStaffStoreOrders({
        page,
        per_page: ITEMS_PER_PAGE,
        ...(branchId > 0 ? { sucursal_id: branchId } : {}),
        ...(filters.status && filters.status !== 'all'
          ? { status: filters.status }
          : {}),
        ...(filters.paymentStatus && filters.paymentStatus !== 'all'
          ? { payment_status: filters.paymentStatus }
          : {}),
        ...(filters.startDate ? { placed_from: filters.startDate } : {}),
        ...(filters.endDate ? { placed_to: filters.endDate } : {}),
      })
      setOrders(result.orders)
      setPagination(result.pagination)
    } catch (loadError) {
      setError(
        getStaffStoreOrdersProblem(
          loadError,
          'No pudimos cargar los pedidos. Intenta nuevamente.',
        ).message,
      )
    } finally {
      setIsLoading(false)
    }
  }, [filters.branch, filters.endDate, filters.startDate, filters.status, filters.paymentStatus, page])

  const loadCancellations = useCallback(async () => {
    if (!canResolveCancellations) return
    setCancellationsLoading(true)
    setCancellationsError('')
    const branchId = Number(cancellationFilters.branch)
    try {
      const result = await listStaffCancellationRequests({
        page: cancellationPage,
        per_page: ITEMS_PER_PAGE,
        ...(branchId > 0 ? { sucursal_id: branchId } : {}),
        ...(cancellationFilters.status &&
          cancellationFilters.status !== 'all'
          ? { status: cancellationFilters.status }
          : {}),
      })
      setCancellations(result.cancellations)
      setCancellationPagination(result.pagination)
    } catch (loadError) {
      setCancellationsError(
        getStaffStoreOrdersProblem(
          loadError,
          'No pudimos cargar las solicitudes de cancelación.',
        ).message,
      )
    } finally {
      setCancellationsLoading(false)
    }
  }, [
    canResolveCancellations,
    cancellationFilters.branch,
    cancellationFilters.status,
    cancellationPage,
  ])

  const loadCashCollections = useCallback(async () => {
    if (!canViewCash) return
    setCashLoading(true)
    setCashError('')
    try {
      const result = await listStoreOrderCashCollections({
        page: cashPage,
        per_page: ITEMS_PER_PAGE,
        ...(cashStatus !== 'all' ? { status: cashStatus } : {}),
      })
      setCashCollections(result.collections)
      setCashPagination(result.pagination)
    } catch (loadError) {
      setCashError(
        getStaffStoreOrdersProblem(
          loadError,
          'No pudimos cargar los cobros domiciliarios.',
        ).message,
      )
    } finally {
      setCashLoading(false)
    }
  }, [canViewCash, cashPage, cashStatus])

  useEffect(() => {
    void loadOrders()
  }, [loadOrders])

  useEffect(() => {
    if (activeTab === 'cancellations') void loadCancellations()
  }, [activeTab, loadCancellations])

  useEffect(() => {
    if (activeTab === 'cash') void loadCashCollections()
  }, [activeTab, loadCashCollections])

  const clearFilters = () => {
    setFilters({
      search: '',
      branch: '',
      status: 'all',
      paymentStatus: 'all',
      startDate: '',
      endDate: '',
    })
    setPage(1)
  }

  const submitDecision = async () => {
    if (!decision) return
    const reason = decisionReason.trim()
    if (decision.action === 'reject' && (reason.length < 5 || reason.length > 500)) {
      setDecisionError('El motivo del rechazo debe tener entre 5 y 500 caracteres.')
      return
    }
    if (reason.length > 0 && (reason.length < 5 || reason.length > 500)) {
      setDecisionError('El motivo debe tener entre 5 y 500 caracteres.')
      return
    }

    setSavingDecision(true)
    setDecisionError('')
    try {
      await decideStaffCancellation(
        decision.cancellation.order_number,
        decision.cancellation.id,
        decision.action,
        reason || undefined,
      )
      setDecision(null)
      setDecisionReason('')
      await Promise.all([loadCancellations(), loadOrders()])
    } catch (saveError) {
      setDecisionError(
        getStaffStoreOrdersProblem(
          saveError,
          'No pudimos resolver la solicitud. Intenta nuevamente.',
        ).message,
      )
    } finally {
      setSavingDecision(false)
    }
  }

  const submitCashAction = async () => {
    if (!cashAction) return
    const amount = cashActionAmount.trim()
    const reason = cashActionReason.trim()
    const remaining = cashRemaining(cashAction.collection)
    const numericAmount = Number(amount)
    const amountInCents = Math.round(numericAmount * 100)
    const remainingInCents = Math.round(remaining * 100)
    if (
      !Number.isFinite(numericAmount) ||
      amountInCents <= 0 ||
      amountInCents > remainingInCents
    ) {
      setCashActionError('El importe debe ser mayor que cero y no superar el saldo pendiente.')
      return
    }
    if (
      cashAction.type === 'discrepancy' &&
      (reason.length < 10 || reason.length > 1000)
    ) {
      setCashActionError('El motivo debe tener entre 10 y 1000 caracteres.')
      return
    }

    setSavingCashAction(true)
    setCashActionError('')
    try {
      if (cashAction.type === 'remit') {
        await remitStoreOrderCash(cashAction.collection.id, amount)
      } else {
        await resolveStoreOrderCashDiscrepancy(cashAction.collection.id, {
          amount,
          reason,
        })
      }
      setCashAction(null)
      setCashActionAmount('')
      setCashActionReason('')
      await Promise.all([loadCashCollections(), loadOrders()])
    } catch (saveError) {
      setCashActionError(
        getStaffStoreOrdersProblem(
          saveError,
          cashAction.type === 'remit'
            ? 'No pudimos registrar la rendición.'
            : 'No pudimos registrar la diferencia.',
        ).message,
      )
    } finally {
      setSavingCashAction(false)
    }
  }

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.branch ||
    (filters.status && filters.status !== 'all') ||
    (filters.paymentStatus && filters.paymentStatus !== 'all') ||
    filters.startDate ||
    filters.endDate,
  )

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Pedidos en línea
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Consulta y administra los pedidos recibidos desde la tienda.
          </p>
        </div>
        <button
          type="button"
          disabled={
            activeTab === 'orders'
              ? isLoading
              : activeTab === 'cancellations'
                ? cancellationsLoading
                : cashLoading
          }
          onClick={() => {
            if (activeTab === 'orders') void loadOrders()
            else if (activeTab === 'cancellations') void loadCancellations()
            else void loadCashCollections()
          }}
          className="w-full rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {(activeTab === 'orders'
            ? isLoading
            : activeTab === 'cancellations'
              ? cancellationsLoading
              : cashLoading)
            ? 'Actualizando...'
            : 'Actualizar'}
        </button>
      </div>

      {((SHOW_CANCELLATION_REQUESTS_TAB && canResolveCancellations) ||
        canViewCash) && (
          <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg border bg-card p-1">
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium ${activeTab === 'orders'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
                }`}
              onClick={() => setActiveTab('orders')}
            >
              Pedidos
            </button>
            {SHOW_CANCELLATION_REQUESTS_TAB && canResolveCancellations && (
              <button
                type="button"
                className={`rounded-md px-4 py-2 text-sm font-medium ${activeTab === 'cancellations'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
                  }`}
                onClick={() => setActiveTab('cancellations')}
              >
                Solicitudes de cancelación
              </button>
            )}
            {canViewCash && (
              <button
                type="button"
                className={`rounded-md px-4 py-2 text-sm font-medium ${activeTab === 'cash'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
                  }`}
                onClick={() => setActiveTab('cash')}
              >
                Cobros en efectivo
              </button>
            )}
          </div>
        )}

      {activeTab === 'orders' ? (
        <>
          <GlobalFilterBar
            show={['search', 'branch', 'status', 'dates']}
            dateShortcuts={['today', 'yesterday', '1week', '1month']}
            values={filters}
            onChange={(changes) => {
              setFilters((current) => ({ ...current, ...changes }))
              setPage(1)
            }}
            customOptions={{
              searchPlaceholder: 'Buscar número de pedido o cliente...',
              statusPlaceholder: 'Estado del pedido',
              statusOptions: ORDER_STATUS_OPTIONS,
            }}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          >
            {/* Filtro de estado de pago */}
            <label className="text-sm font-medium text-foreground">
              <span className="sr-only">Estado de pago</span>
              <select
                value={filters.paymentStatus || 'all'}
                onChange={(e) => {
                  setFilters((current) => ({ ...current, paymentStatus: e.target.value }))
                  setPage(1)
                }}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-normal"
              >
                <option value="all">Todos los estados</option>
                {PAYMENT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </GlobalFilterBar>

          {error && (
            <ErrorPanel message={error} onRetry={() => void loadOrders()} />
          )}
          {isLoading ? (
            <TableSkeleton columns={10} rows={5} />
          ) : !error ? (
            <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                    <tr>
                      {[
                        'Número de pedido',
                        'Sucursal',
                        'Total',
                        'Estado',
                        'Estado de pago',
                        'Método de pago',
                        'Tipo de entrega',
                        'Cliente',
                        'Fecha y hora',
                        'Acciones',
                      ].map((label) => (
                        <th
                          key={label}
                          className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium uppercase"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {orders.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-6 py-10 text-center text-sm text-muted-foreground"
                        >
                          No se encontraron pedidos con los filtros actuales.
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => {
                        const placed = formatStoreOrderDate(
                          order.created_at,
                        )
                        return (
                          <tr
                            key={order.order_number}
                            className="transition-colors hover:bg-table-row-hover"
                          >
                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                              <span
                                className="font-medium text-table-foreground"
                                title={order.order_number}
                              >
                                {order.order_number}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm">
                              {order.sucursal?.name || 'Por confirmar'}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold">
                              {formatStoreOrderMoney(
                                order.amounts.total_amount,
                                order.currency_code,
                              )}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <Badge className={getOrderStatusBadgeClass(order.fulfillment_status || order.status)}>
                                {getStaffOrderStatusLabel(
                                  order.fulfillment_status || order.status,
                                )}
                              </Badge>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <Badge className={getPaymentStatusBadgeClass(order.payment_status)}>
                                {getStaffOrderStatusLabel(order.payment_status)}
                              </Badge>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm">
                              {getPaymentMethodsLabel(order)}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm">
                              {getFulfillmentTypeLabel(order.fulfillment_type)}
                            </td>
                            <td
                              className="max-w-64 truncate px-6 py-4 text-sm"
                              title={order.customer.name}
                            >
                              {order.customer.name}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm">
                              <div className="font-medium">{placed.date}</div>
                              <div className="text-xs text-table-foreground/70">
                                {placed.time}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <button
                                type="button"
                                className="inline-flex rounded p-1 hover:bg-table-row-hover"
                                title="Ver detalle"
                                aria-label={`Ver detalle del pedido ${order.order_number}`}
                                onClick={() =>
                                  setSelectedOrderNumber(order.order_number)
                                }
                              >
                                <Eye className="h-4 w-4 text-card-foreground" />
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <GlobalPaginator
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                currentPage={pagination.current_page}
                from={pagination.from ?? 0}
                to={pagination.to ?? 0}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </>
      ) : activeTab === 'cancellations' ? (
        <>
          <GlobalFilterBar
            show={['branch']}
            values={cancellationFilters}
            onChange={(changes) => {
              setCancellationFilters((current) => ({ ...current, ...changes }))
              setCancellationPage(1)
            }}
          >
            <label className="text-sm font-medium text-foreground">
              Estado de la solicitud
              <select
                value={cancellationFilters.status || 'REQUESTED'}
                onChange={(event) => {
                  setCancellationFilters((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                  setCancellationPage(1)
                }}
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 font-normal"
              >
                {CANCELLATION_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </GlobalFilterBar>
          {cancellationsError && (
            <ErrorPanel
              message={cancellationsError}
              onRetry={() => void loadCancellations()}
            />
          )}
          {cancellationsLoading ? (
            <TableSkeleton columns={6} rows={5} />
          ) : !cancellationsError ? (
            <div className="overflow-hidden rounded-lg border border-border bg-table-bg shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border bg-table-header-bg">
                    <tr>
                      {[
                        'Pedido',
                        'Sucursal',
                        'Solicitante',
                        'Motivo',
                        'Estado',
                        'Acciones',
                      ].map((label) => (
                        <th
                          key={label}
                          className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium uppercase"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cancellations.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-10 text-center text-sm text-muted-foreground"
                        >
                          No hay solicitudes con los filtros actuales.
                        </td>
                      </tr>
                    ) : (
                      cancellations.map((cancellation) => (
                        <tr key={cancellation.id}>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            <span className="font-medium text-table-foreground">
                              {cancellation.order_number}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            {cancellation.sucursal?.name || 'Por confirmar'}
                          </td>
                          <td className="max-w-52 break-words px-5 py-4 text-sm">
                            {cancellation.requester_name || 'Cliente'}
                          </td>
                          <td className="max-w-80 break-words px-5 py-4 text-sm">
                            {cancellation.reason}
                          </td>
                          <td className="whitespace-nowrap px-5 py-4">
                            <Badge className="bg-slate-100 text-slate-700">
                              {getStaffOrderStatusLabel(cancellation.status)}
                            </Badge>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="rounded p-1 hover:bg-table-row-hover"
                                title="Ver detalle"
                                aria-label={`Ver detalle del pedido ${cancellation.order_number}`}
                                onClick={() =>
                                  setSelectedOrderNumber(
                                    cancellation.order_number,
                                  )
                                }
                              >
                                <Eye className="h-4 w-4 text-card-foreground" />
                              </button>
                              {cancellation.status === 'REQUESTED' ? (
                                <>
                                  <button
                                    type="button"
                                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                                    onClick={() => {
                                      setDecisionReason('')
                                      setDecisionError('')
                                      setDecision({
                                        cancellation,
                                        action: 'approve',
                                      })
                                    }}
                                  >
                                    Aprobar
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                                    onClick={() => {
                                      setDecisionReason('')
                                      setDecisionError('')
                                      setDecision({
                                        cancellation,
                                        action: 'reject',
                                      })
                                    }}
                                  >
                                    Rechazar
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  Resuelta
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <GlobalPaginator
                totalItems={cancellationPagination.total}
                itemsPerPage={cancellationPagination.per_page}
                currentPage={cancellationPagination.current_page}
                from={cancellationPagination.from ?? 0}
                to={cancellationPagination.to ?? 0}
                onPageChange={setCancellationPage}
              />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <label className="block max-w-sm text-sm font-medium">
              Estado del cobro
              <select
                value={cashStatus}
                onChange={(event) => {
                  setCashStatus(event.target.value)
                  setCashPage(1)
                }}
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 font-normal"
              >
                {CASH_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {cashError && (
            <ErrorPanel
              message={cashError}
              onRetry={() => void loadCashCollections()}
            />
          )}
          {cashLoading ? (
            <TableSkeleton columns={8} rows={5} />
          ) : !cashError ? (
            <div className="overflow-hidden rounded-lg border border-border bg-table-bg shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border bg-table-header-bg">
                    <tr>
                      {[
                        'Pedido',
                        'Fecha',
                        'Cobrado',
                        'Rendido',
                        'Diferencia',
                        'Pendiente',
                        'Estado',
                        'Acciones',
                      ].map((label) => (
                        <th
                          key={label}
                          className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium uppercase"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cashCollections.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-6 py-10 text-center text-sm text-muted-foreground"
                        >
                          No hay cobros con el estado seleccionado.
                        </td>
                      </tr>
                    ) : (
                      cashCollections.map((collection) => {
                        const collected = formatStoreOrderDate(
                          collection.collected_at,
                        )
                        const remaining = cashRemaining(collection)
                        const canAct = remaining > 0
                        return (
                          <tr key={collection.id}>
                            <td className="whitespace-nowrap px-5 py-4 text-sm font-medium">
                              {collection.order_number || 'Por confirmar'}
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-sm">
                              <div>{collected.date}</div>
                              <div className="text-xs text-muted-foreground">
                                {collected.time}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-sm">
                              {formatStoreOrderMoney(collection.amount)}
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-sm">
                              {formatStoreOrderMoney(collection.remitted_amount)}
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-sm">
                              {formatStoreOrderMoney(collection.shortfall_amount)}
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold">
                              {formatStoreOrderMoney(remaining.toFixed(2))}
                            </td>
                            <td className="whitespace-nowrap px-5 py-4">
                              <Badge className="bg-slate-100 text-slate-700">
                                {getStaffOrderStatusLabel(collection.status)}
                              </Badge>
                            </td>
                            <td className="whitespace-nowrap px-5 py-4">
                              <div className="flex items-center gap-2">
                                {collection.order_number && (
                                  <button
                                    type="button"
                                    className="rounded p-1 hover:bg-table-row-hover"
                                    title="Ver pedido"
                                    aria-label={`Ver pedido ${collection.order_number}`}
                                    onClick={() =>
                                      setSelectedOrderNumber(
                                        collection.order_number,
                                      )
                                    }
                                  >
                                    <Eye className="h-4 w-4 text-card-foreground" />
                                  </button>
                                )}
                                {canRemitCash && canAct && (
                                  <button
                                    type="button"
                                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                                    onClick={() => {
                                      setCashActionAmount(remaining.toFixed(2))
                                      setCashActionReason('')
                                      setCashActionError('')
                                      setCashAction({ collection, type: 'remit' })
                                    }}
                                  >
                                    Rendir
                                  </button>
                                )}
                                {canResolveCash && canAct && (
                                  <button
                                    type="button"
                                    className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800"
                                    onClick={() => {
                                      setCashActionAmount(remaining.toFixed(2))
                                      setCashActionReason('')
                                      setCashActionError('')
                                      setCashAction({
                                        collection,
                                        type: 'discrepancy',
                                      })
                                    }}
                                  >
                                    Registrar diferencia
                                  </button>
                                )}
                                {!canAct && (
                                  <span className="text-xs text-muted-foreground">
                                    Conciliado
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <GlobalPaginator
                totalItems={cashPagination.total}
                itemsPerPage={cashPagination.per_page}
                currentPage={cashPagination.current_page}
                from={cashPagination.from ?? 0}
                to={cashPagination.to ?? 0}
                onPageChange={setCashPage}
              />
            </div>
          ) : null}
        </>
      )}

      <StoreOrderDetailModal
        open={Boolean(selectedOrderNumber)}
        orderNumber={selectedOrderNumber}
        onClose={() => setSelectedOrderNumber(null)}
        onOrderUpdated={() => {
          void loadOrders()
          if (activeTab === 'cancellations') void loadCancellations()
          if (activeTab === 'cash') void loadCashCollections()
        }}
      />

      {decision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-xl bg-card p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-card-foreground">
              {decision.action === 'approve'
                ? 'Aprobar cancelación'
                : 'Rechazar cancelación'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pedido {decision.cancellation.order_number}
            </p>
            <label className="mt-5 block text-sm font-medium">
              Motivo {decision.action === 'reject' ? '(obligatorio)' : '(opcional)'}
              <textarea
                value={decisionReason}
                maxLength={500}
                rows={4}
                className="mt-2 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                onChange={(event) => setDecisionReason(event.target.value)}
              />
            </label>
            {decisionError && (
              <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
                {decisionError}
              </p>
            )}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-md border px-4 py-2 text-sm font-medium"
                disabled={savingDecision}
                onClick={() => setDecision(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                disabled={savingDecision}
                onClick={() => void submitDecision()}
              >
                {savingDecision ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cashAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-xl bg-card p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-card-foreground">
              {cashAction.type === 'remit'
                ? 'Rendir efectivo en caja'
                : 'Registrar diferencia de efectivo'}
            </h2>
            <p className="mt-1 break-words text-sm text-muted-foreground">
              Pedido {cashAction.collection.order_number || 'por confirmar'} ·
              Saldo pendiente{' '}
              {formatStoreOrderMoney(
                cashRemaining(cashAction.collection).toFixed(2),
              )}
            </p>
            <label className="mt-5 block text-sm font-medium">
              Importe <span className="text-red-600" aria-hidden="true">*</span>
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                value={cashActionAmount}
                onChange={(event) => setCashActionAmount(event.target.value)}
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
            {cashAction.type === 'remit' ? (
              <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                La caja debe estar activa, abierta y pertenecer a la sucursal del pedido.
              </p>
            ) : (
              <label className="mt-4 block text-sm font-medium">
                Motivo de la diferencia{' '}
                <span className="text-red-600" aria-hidden="true">*</span>
                <textarea
                  required
                  value={cashActionReason}
                  maxLength={1000}
                  rows={4}
                  onChange={(event) => setCashActionReason(event.target.value)}
                  className="mt-2 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <span className="mt-1 block text-right text-xs text-muted-foreground">
                  {cashActionReason.length}/1000
                </span>
              </label>
            )}
            {cashActionError && (
              <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
                {cashActionError}
              </p>
            )}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-md border px-4 py-2 text-sm font-medium"
                disabled={savingCashAction}
                onClick={() => setCashAction(null)}
              >
                Volver
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                disabled={savingCashAction}
                onClick={() => void submitCashAction()}
              >
                {savingCashAction ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
      <p className="text-sm font-medium text-red-700">{message}</p>
      <button
        type="button"
        className="mt-3 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
        onClick={onRetry}
      >
        Reintentar
      </button>
    </div>
  )
}
